import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { QuestionType, QuestionComponentProps } from '../types';
import { YesNoQuestion } from './questions/YesNoQuestion';
import { SingleSelectQuestion } from './questions/SingleSelectQuestion';
import { MultiSelectQuestion } from './questions/MultiSelectQuestion';
import { TextQuestion } from './questions/TextQuestion';
import { NumberQuestion } from './questions/NumberQuestion';
import { RangeQuestion } from './questions/RangeQuestion';

// Styled components
const QuestionContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minHeight: '300px', // Reduced minimum height for better mobile experience
  justifyContent: 'center',
  padding: '16px', // Add padding for mobile
  [theme.breakpoints.up('md')]: {
    minHeight: '400px', // Still maintain larger height on desktop
    padding: '24px',
  },
}));

const ErrorContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '32px',
  textAlign: 'center',
}));

const ErrorTitle = styled(Typography)(() => ({
  fontSize: '18px',
  lineHeight: '24px',
  fontWeight: 500,
  color: '#F44336',
  marginBottom: '8px',
}));

const ErrorMessage = styled(Typography)(() => ({
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 400,
  color: '#666666',
}));

interface QuestionRendererProps extends QuestionComponentProps {
  // Additional props specific to the renderer
}

export const QuestionRenderer: React.FC<QuestionRendererProps> = (props) => {
  const { question, language } = props;

  const getErrorMessage = (): string => {
    return language === 'no' ? 'Ukjent spørsmålstype' : 'Unknown question type';
  };

  const getErrorDescription = (type: string): string => {
    return language === 'no'
      ? `Spørsmålstype "${type}" støttes ikke.`
      : `Question type "${type}" is not supported.`;
  };

  // Render the appropriate question component based on type
  const renderQuestion = () => {
    switch (question.type) {
      case QuestionType.YES_NO:
        return <YesNoQuestion {...props} />;

      case QuestionType.SINGLE_SELECT:
        return <SingleSelectQuestion {...props} />;

      case QuestionType.MULTI_SELECT:
        return <MultiSelectQuestion {...props} />;

      case QuestionType.TEXT:
        return <TextQuestion {...props} />;

      case QuestionType.NUMBER:
        return <NumberQuestion {...props} />;

      case QuestionType.RANGE:
        return <RangeQuestion {...props} />;

      default:
        return (
          <ErrorContainer>
            <ErrorTitle>{getErrorMessage()}</ErrorTitle>
            <ErrorMessage>{getErrorDescription(question.type)}</ErrorMessage>
          </ErrorContainer>
        );
    }
  };

  return (
    <QuestionContainer data-testid={`question-${question.type.toLowerCase()}`}>
      {renderQuestion()}
    </QuestionContainer>
  );
};
